import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { AvatarSize } from 'components/Avatar';
import { theme, toRem } from 'design';
import { Avatar } from '../Avatar';
import { Text } from '../Typography';
import { IUserProps } from './UserProps';

interface IProps extends IUserProps {
  className?: string;
  isLoading?: boolean;
  size?: AvatarSize;
  stackable?: boolean;
  wrapText?: boolean;
  disabled?: boolean;
  userId?: string;
  details?: string;
}

const Layout = styled.div<Partial<IProps>>`
  flex-grow: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  height: auto;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-flow: row nowrap;
    justify-content: flex-start;
  }

  ${({ stackable }) =>
    stackable &&
    css`
      flex-flow: column nowrap;
      justify-content: center;

      @media (min-width: ${theme.breakpoint.small}px) {
        flex-flow: row nowrap;
        justify-content: flex-start;
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.75;
    `}
`;

const UserInfo = styled.section<Partial<IProps>>`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0 0 0 ${toRem(11)};

  ${({ stackable }) =>
    stackable &&
    css`
      align-items: center;
      margin: ${toRem(11)} 0;

      @media (min-width: ${theme.breakpoint.small}px) {
        align-items: flex-start;
        margin: 0 0 0 ${toRem(11)};
      }
    `}
`;

const WrapTextStyles = css`
  word-break: break-word;
  white-space: normal;
  min-width: ${toRem(48)};
`;

const Name = styled(Text).attrs({
  size: 'small',
})<Partial<IProps>>`
  font-weight: ${theme.font.weight.regular};
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  ${({ wrapText }) =>
    wrapText &&
    css`
      ${WrapTextStyles}
    `}
`;

const Details = styled(Text).attrs({
  size: 'xsmall',
})<Partial<IProps>>`
  margin-top: ${toRem(3)};
  word-break: break-word;
  white-space: normal;
  color: ${theme.color.neutral[700]};

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-top: ${toRem(5.5)};
  }

  ${({ wrapText }) =>
    wrapText &&
    css`
      ${WrapTextStyles}
    `}
`;

const User = ({ id, className, hasPaymentMethod, isDismissed, image, isLoading, role, site, size = 'medium', stackable = false, username, wrapText, disabled = false, userId, details }: IProps) => (
  <Layout className={className} stackable={stackable} disabled={disabled}>
    <Avatar id={id} size={size} image={image} userId={userId} username={username} hasPaymentMethod={hasPaymentMethod} isDismissed={isDismissed} isLoading={isLoading} />

    <UserInfo stackable={stackable} data-cy="user-info">
      <Name data-cy="user-name" wrapText={wrapText}>
        {isLoading ? <Skeleton width={77} /> : username}
      </Name>

      {details && (
        <Details data-cy="user-detail" wrapText={wrapText}>
          {isLoading ? <Skeleton width={77} /> : details}
        </Details>
      )}

      {(site || role) && (
        <Details data-cy="user-site-role" wrapText={wrapText}>
          {isLoading ? (
            <Skeleton width={77} />
          ) : (
            <>
              {site && site}
              {site && role && ', '}
              {role && role}
            </>
          )}
        </Details>
      )}
    </UserInfo>
  </Layout>
);

export default User;
